<template>
  <v-card>
    <v-card-title>Artikel hinzufügen</v-card-title>
    <v-card-text>
      <v-text-field
        v-model="sWord"
        variant="solo"
        label="Suche"
        @keypress.enter="doSearch(true)"
        append-inner-icon="mdi-magnify"
        @click:append-inner="doSearch(true)"
        :loading="loading"
        autofocus
      />
      <v-list>
        <v-list-item v-for="item in items" :key="item.ArtikelNr" @click="selectItem(item)">

          <v-list-item-title>
            <ScrollText v-if="item.Bezeichnung1" :text="item.Bezeichnung1"/>
          </v-list-item-title>
          <v-list-item-subtitle>
            <ScrollText v-if="item.Bezeichnung2" :text="item.Bezeichnung2"/>
          </v-list-item-subtitle>

          <template #append>
            <v-list-item-action>
              <div class="d-flex align-center">
                <v-progress-circular v-if="selectedId === item.ArtikelNr" indeterminate/>
                <v-list-item-subtitle class="ml-2">{{ item.ArtikelNr }}</v-list-item-subtitle>
              </div>
            </v-list-item-action>
          </template>
        </v-list-item>
      </v-list>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import {computed, defineComponent, ref, watch} from 'vue';
import {doAlert} from '@/util';
import {ApiArticle} from '@/model/Article';
import {findArticles, getArticle} from '@/api/module/article';
import {usePicklistPickerStore} from '@/store/picklistPickerV2';
import ScrollText from '@/components/ScrollText.vue';

export default defineComponent({
  name: 'ArticleSearch',
  components: {ScrollText},
  props: {
    modelValue: {
      type: String,
      default: '',
    },
  },
  emits: {
    'update:modelValue'(val: string) {
      return typeof val === 'string';
    },
    select() {
      return true;
    },
  },
  setup(props, ctx) {
    const sWord = computed({
      get: () => props.modelValue,
      set(val: string) {
        ctx.emit('update:modelValue', val);
      },
    });
    const items = ref([] as ApiArticle[]);
    const loading = ref(false);
    const selectedId = ref<string | null>(null);


    let searchedString: string | null = null;

    const selectItem = async (item: ApiArticle, emit = true) => {
      if (selectedId.value) return;
      selectedId.value = item.ArtikelNr;
      try {
        const [fullArticle] = await getArticle(item.ArtikelNr);
        const picklistPicker = usePicklistPickerStore();

        if (!fullArticle) throw new Error('Artikel konnte nicht gefunden werden');

        await picklistPicker.addPositionFromArticle(fullArticle);
        if (emit) ctx.emit('select');
      } catch (err) {
        await doAlert((err as Error).message, 'Fehler');
      } finally {
        selectedId.value = null;
      }
    };

    const doSearch = async (force: boolean): Promise<number> => {
      if ((searchedString === sWord.value && !force) || loading.value) return items.value.length;
      searchedString = sWord.value;
      if (sWord.value === '' || (sWord.value.length < 3 && !force)) {
        items.value = [];
        return 0;
      }
      loading.value = true;
      let selected = false;
      try {
        const [newItems] = await findArticles(sWord.value);
        items.value = newItems;
        if (force && items.value.length === 1) {
          await selectItem(items.value[0]);
          selected = true;
        }

        return items.value.length;
      } finally {
        loading.value = false;
        if (selected) ctx.emit('select');
      }
    };

    let searchTimeout: number | null = null;
    const debounceSearch = () => {
      if (searchTimeout) clearTimeout(searchTimeout);

      searchTimeout = setTimeout(doSearch, 1000);
    };
    watch(sWord, debounceSearch);


    return {
      sWord,
      items,
      loading,
      selectedId,
      doSearch,
      selectItem,
    };
  },
});
</script>
